import { useState } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { financialApi, type Fine } from '../api';
import { PageHeader } from '@/lib/ui';

function money(minor: number, currency = 'AED') {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}

const statusClass: Record<Fine['status'], string> = {
  ISSUED: 'bg-amber-500/15 text-amber-300',
  DEDUCTED: 'bg-emerald-500/15 text-emerald-300',
  WAIVED: 'bg-slate-500/15 text-slate-300',
};

export function FinesPage() {
  const [userId, setUserId] = useState('');
  const [fines, setFines] = useState<Fine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showIssue, setShowIssue] = useState(false);

  const load = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      setFines(await financialApi.listFines(userId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const waive = async (f: Fine) => {
    const note = prompt('Waiver note (optional)') ?? '';
    await financialApi.waiveFine(f.id, note || undefined);
    await load();
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Fines"
        icon={AlertTriangle}
        subtitle="Fines are deducted at the next payroll finalize."
        actions={
          <button className="btn-primary" onClick={() => setShowIssue(true)}>
            <Plus className="h-4 w-4" />
            Issue fine
          </button>
        }
      />

      <div className="mb-4 flex gap-2">
        <input
          className="input flex-1 font-mono"
          placeholder="field_user_id (uuid)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <button className="btn-secondary" onClick={load} disabled={busy || !userId}>
          Load
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded border border-border bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-accent text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Issued</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {fines.map((f) => (
              <tr key={f.id} className="hover:bg-surface-accent">
                <td className="px-3 py-2 font-mono">{f.issued_on}</td>
                <td className="px-3 py-2">{f.reason}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {money(f.amount_minor, f.currency)}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-xs ${statusClass[f.status]}`}>
                    {f.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {f.status === 'ISSUED' && (
                    <button className="text-xs text-amber-300" onClick={() => waive(f)}>
                      waive
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {fines.length === 0 && userId && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No fines for this user.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showIssue && (
        <IssueFineModal
          defaultUserId={userId}
          onCancel={() => setShowIssue(false)}
          onSaved={() => {
            setShowIssue(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function IssueFineModal({
  defaultUserId,
  onCancel,
  onSaved,
}: {
  defaultUserId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [userId, setUserId] = useState(defaultUserId);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await financialApi.issueFine({
        field_user_id: userId,
        amount_minor: Math.round(Number(amount) * 100),
        reason,
        issued_on: issuedOn,
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message || 'Issue failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-96 rounded border border-border bg-surface p-6">
        <h3 className="mb-4 text-lg font-semibold">Issue fine</h3>
        {err && (
          <div className="mb-3 rounded bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>
        )}
        <div className="space-y-3">
          <Field label="field_user_id">
            <input
              className="input w-full font-mono"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </Field>
          <Field label="Amount (AED)">
            <input
              className="input w-full font-mono"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field label="Reason">
            <input
              className="input w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <Field label="Issued on">
            <input
              className="input w-full"
              type="date"
              value={issuedOn}
              onChange={(e) => setIssuedOn(e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !userId || !amount || !reason}
          >
            Issue
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
